import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { Button, CircularLoader, TextButton, Typography } from 'seagull';
import OTPVerificationField from '~/components/OTPVerificationField/OTPVerificationField';
import { routes } from '~/constants';
import { useEventReporter, useIoContext, useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { LoginMethod, useSendLoginGtmEvents } from '~/hooks/useSendLoginGtmEvents';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { usePostAuthRedirect } from '~/utils/auth-redirect';
import { ErrorMessage, FormContainer, FormFields, VerticalFormFields } from '../../components/FormComponents';
import { FormSeparatorHorizontal } from '../../components/FormSeparator';
import { LoginFlow } from '../LoginScreen.types';
import styles from './OTPForm.module.scss';
export default function OTPForm(param) {
    let { email, flow, onBack, shouldRequestOTP } = param;
    const { t: texts } = useLocaleKeys('pages.auth.otp');
    const { reportFormSubmission, reportToHeap } = useEventReporter();
    const { browser: { changeLocation } } = useIoContext();
    const performPostAuthRedirect = usePostAuthRedirect();
    const { api, logBI } = useStoreActions();
    const getUtms = useUtmsParams();
    const [otpSessionStatus, setOtpSessionStatus] = useState();
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const otpFieldRef = useRef(null);
    const { sendGtmLoginSuccessEvent, sendGtmLoginFailEvent } = useSendLoginGtmEvents();
    const notices = {
        otpRequestOK: {
            text: texts.newCodeSent(),
            displaysInline: false,
            isError: false
        },
        tooManyRequests: {
            text: texts.tooManyRequests(),
            displaysInline: false,
            isError: true
        },
        wrongCode: {
            text: texts.wrongCode(),
            displaysInline: true,
            isError: true
        },
        unknown: {
            text: texts.unknownError(),
            displaysInline: false,
            isError: true
        }
    };
    const notice = notices[otpSessionStatus !== null && otpSessionStatus !== void 0 ? otpSessionStatus : ''];
    const inlineError = (notice === null || notice === void 0 ? void 0 : notice.isError) && (notice === null || notice === void 0 ? void 0 : notice.displaysInline) && (notice === null || notice === void 0 ? void 0 : notice.text);
    const floatingError = (notice === null || notice === void 0 ? void 0 : notice.isError) && !(notice === null || notice === void 0 ? void 0 : notice.displaysInline) && (notice === null || notice === void 0 ? void 0 : notice.text);
    const floatingMessage = !(notice === null || notice === void 0 ? void 0 : notice.isError) && (notice === null || notice === void 0 ? void 0 : notice.text);
    useOnMount(()=>{
        async function requestOTPOnMount() {
            if (shouldRequestOTP) {
                const result = flow === LoginFlow.EmailVerification ? await handlePromise(api.auth.requestNewOTPForSignupFlow(email)) : await handlePromise(api.auth.requestNewOTP(email));
                if (!result.success) {
                    setOtpSessionStatus(getErrorCause(result.error));
                }
            }
        }
        requestOTPOnMount();
    });
    async function validateOTP(otp) {
        setIsLoading(true);
        const result = flow === LoginFlow.EmailVerification ? await handlePromise(api.auth.validateOTPForSignupFlow(email, otp, getUtms())) : await handlePromise(api.auth.validateOTP(email, otp, getUtms()));
        if (!result.success) {
            setOtpSessionStatus(getErrorCause(result.error));
            setHasError(true);
            setIsLoading(false);
            sendGtmLoginFailEvent(LoginMethod.OTP);
        } else {
            reportFormSubmission('general-logged-in-successfully', null);
            reportToHeap('Login Screen', 'click', 'signed-in-with-six-digits-code', {
                email
            });
            logBI(16001, {
                email
            });
            await sendGtmLoginSuccessEvent(LoginMethod.OTP);
            if (flow === LoginFlow.EmailVerification) {
                changeLocation(routes.home);
            } else {
                performPostAuthRedirect();
            }
        }
    }
    async function requestNewOTP() {
        logBI(16000, {
            email
        });
        reportToHeap('Login Screen', 'click', 'clicked-send-me-six-digits-code', {
            email
        });
        const result = flow === LoginFlow.EmailVerification ? await handlePromise(api.auth.requestNewOTPForSignupFlow(email)) : await handlePromise(api.auth.requestNewOTP(email));
        if (!result.success) {
            setOtpSessionStatus(getErrorCause(result.error));
        } else {
            setOtpSessionStatus('otpRequestOK');
        }
    }
    function getErrorCause(error) {
        if (error.response) {
            if (error.response.status === 400) {
                return 'wrongCode';
            } else if (error.response.status === 429) {
                return 'tooManyRequests';
            }
        }
        return 'unknown';
    }
    return /*#__PURE__*/ _jsxs(FormContainer, {
        onSubmit: (e)=>e.preventDefault(),
        "data-testid": "otp-form",
        className: styles.otpForm,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                testId: "otp-form-title",
                variant: "h5",
                weight: "normal",
                className: styles.title,
                children: flow === LoginFlow.EmailVerification ? texts.verifyYourEmail() : texts.checkYourEmail()
            }),
            /*#__PURE__*/ _jsx(FormFields, {
                className: styles.formFields,
                children: /*#__PURE__*/ _jsxs(VerticalFormFields, {
                    className: styles.verticalFormFields,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.subtitle,
                            children: /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    texts.enterCodeSentTo(),
                                    ' ',
                                    /*#__PURE__*/ _jsx(Typography, {
                                        as: "span",
                                        weight: "bold",
                                        children: email
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(OTPVerificationField, {
                            ref: otpFieldRef,
                            onChange: validateOTP,
                            hasError: hasError,
                            onInputChange: ()=>{
                                if (notice === null || notice === void 0 ? void 0 : notice.isError) {
                                    setOtpSessionStatus(undefined);
                                }
                            }
                        }),
                        isLoading && /*#__PURE__*/ _jsx("div", {
                            className: styles.loadingContainer,
                            "data-testid": "loading-container",
                            children: /*#__PURE__*/ _jsx(CircularLoader, {
                                size: 24,
                                centeredInBox: true
                            })
                        }),
                        inlineError && /*#__PURE__*/ _jsx(ErrorMessage, {
                            testId: "error-message",
                            children: inlineError
                        }),
                        floatingMessage && /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            testId: "info-message",
                            className: styles.floatingMessage,
                            children: floatingMessage
                        }),
                        floatingError && /*#__PURE__*/ _jsx(ErrorMessage, {
                            testId: "error-message",
                            children: floatingError
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.footer,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.newCodeContent,
                        children: [
                            /*#__PURE__*/ _jsx(FormSeparatorHorizontal, {
                                className: styles.separator,
                                children: texts.couldntFindInInbox()
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'request_new_otp_code'
                                },
                                skin: "primaryOutline",
                                testId: "get-new-code-button",
                                size: "medium",
                                underline: "none",
                                hoverSkin: "primaryOutline",
                                className: styles.getNewCodeButton,
                                onClick: async (e)=>{
                                    var _otpFieldRef_current;
                                    e.preventDefault();
                                    requestNewOTP();
                                    (_otpFieldRef_current = otpFieldRef.current) === null || _otpFieldRef_current === void 0 ? void 0 : _otpFieldRef_current.reset();
                                },
                                children: texts.getNewCode()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(TextButton, {
                        underline: "none",
                        skin: "secondary",
                        onClick: onBack,
                        testId: "back-link",
                        className: styles.backButton,
                        children: texts.back()
                    })
                ]
            })
        ]
    });
}
